import { createSlice } from '@reduxjs/toolkit';
import Cookies from "universal-cookie";

const cookies = new Cookies();
// Initialize state from cookies if available
const getInitialState = () => {
    return {
        // Core Wialon session data
        sid: cookies.get('sid') && cookies.get('sid') !== 'null' ? cookies.get('sid') : null,
        baseUrl: cookies.get('baseUrl') && cookies.get('baseUrl') !== 'null' ? cookies.get('baseUrl') : null,
        hostUrl: cookies.get('hostUrl') && cookies.get('hostUrl') !== 'null' ? cookies.get('hostUrl') : null,
        authHash: cookies.get('authHash') && cookies.get('authHash') !== 'null' ? cookies.get('authHash') : null,

        // User data
        userId: cookies.get('user_id') || null,
        userName: cookies.get('user_name') || null,

        // Account data
        accountId: cookies.get('account_id') || null,
        accountName: cookies.get('account_name') || null,

        // Session status
        isAuthenticated: cookies.get('active_session') === 'yes',
        isSessionActive: cookies.get('active_session') === 'yes',
        lastKeepAlive: null,

        // Loading and error states
        isLoading: false,
        isInitializing: true,
        error: null,

        // Keep-alive status
        keepAliveFailCount: 0,
        maxKeepAliveFailures: 3,
    };
};

export const wialonSessionSlice = createSlice({
    name: 'wialonSession',
    initialState: getInitialState(),
    reducers: {
        // Set initial URL parameters
        setUrlParams: (state, action) => {
            const { sid, baseUrl, hostUrl, authHash } = action.payload;
            state.baseUrl = baseUrl;
            state.hostUrl = hostUrl;
            state.authHash = authHash;
            // Note: sid will be updated after duplicate session call

            // Sync to cookies
            cookies.set('baseUrl', baseUrl, { path: '/' });
            cookies.set('hostUrl', hostUrl, { path: '/' });
            cookies.set('authHash', authHash, { path: '/' });
        },

        // Set session after successful authentication
        setSession: (state, action) => {
            const { sid, userId, userName } = action.payload;
            state.sid = sid;
            state.userId = userId;
            state.userName = userName;
            state.isAuthenticated = true;
            state.isSessionActive = true;
            state.error = null;
            state.keepAliveFailCount = 0;

            // Sync to cookies
            cookies.set('sid', sid, { path: '/' });
            cookies.set('user_id', userId, { path: '/' });
            cookies.set('user_name', userName, { path: '/' });
            cookies.set('active_session', 'yes', { path: '/' });
        },

        // Set account info
        setAccountInfo: (state, action) => {
            const { accountId, accountName } = action.payload;
            state.accountId = accountId;
            state.accountName = accountName;

            // Sync to cookies
            cookies.set('account_id', accountId, { path: '/' });
            cookies.set('account_name', accountName, { path: '/' });
        },

        // Update keep-alive status
        keepAliveSuccess: (state) => {
            state.isSessionActive = true;
            state.lastKeepAlive = Date.now();
            state.keepAliveFailCount = 0;
            state.error = null;
            cookies.set('active_session', 'yes', { path: '/' });
        },

        // Handle keep-alive failure
        keepAliveFailure: (state, action) => {
            state.keepAliveFailCount += 1;
            state.error = action.payload?.error || 'Keep-alive failed';

            // If max failures reached, mark session as inactive
            if (state.keepAliveFailCount >= state.maxKeepAliveFailures) {
                state.isSessionActive = false;
                cookies.set('active_session', 'no', { path: '/' });
            }
        },

        // Session expired
        sessionExpired: (state, action) => {
            state.sid = null; // Important: Clear sid so keep-alive stops
            state.isSessionActive = false;
            state.isAuthenticated = false;
            state.error = action.payload?.error || 'Session expired';
            cookies.set('active_session', 'no', { path: '/' });
            cookies.remove('sid', { path: '/' });
        },

        // Clear session (logout)
        clearSession: (state) => {
            state.sid = null;
            state.userId = null;
            state.userName = null;
            state.accountId = null;
            state.accountName = null;
            state.isAuthenticated = false;
            state.isSessionActive = false;
            state.lastKeepAlive = null;
            state.error = null;
            state.keepAliveFailCount = 0;

            // Clear cookies using remove
            cookies.remove('sid', { path: '/' });
            cookies.set('active_session', 'no', { path: '/' });
            cookies.remove('user_id', { path: '/' });
            cookies.remove('user_name', { path: '/' });
            cookies.remove('account_id', { path: '/' });
            cookies.remove('account_name', { path: '/' });
            cookies.remove('authHash', { path: '/' });
        },

        // Loading states
        setLoading: (state, action) => {
            state.isLoading = action.payload;
        },

        setInitializing: (state, action) => {
            state.isInitializing = action.payload;
        },

        // Error handling
        setError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },

        clearError: (state) => {
            state.error = null;
        },
    },
});

export const {
    setUrlParams,
    setSession,
    setAccountInfo,
    keepAliveSuccess,
    keepAliveFailure,
    sessionExpired,
    clearSession,
    setLoading,
    setInitializing,
    setError,
    clearError,
} = wialonSessionSlice.actions;

// Selectors
export const selectWialonSession = (state) => state.wialonSession;
export const selectIsAuthenticated = (state) => state.wialonSession.isAuthenticated;
export const selectIsSessionActive = (state) => state.wialonSession.isSessionActive;
export const selectSid = (state) => state.wialonSession.sid;
export const selectBaseUrl = (state) => state.wialonSession.baseUrl;
export const selectSessionError = (state) => state.wialonSession.error;

export default wialonSessionSlice.reducer;
